/**
 * Building, comparing and reading back the scope a request hands the view (types/viewScope.ts).
 */

import type { Bookmark } from '@/types/bookmarks';
import type { ViewScope } from '@/types/viewScope';
import { ViewScopeKind } from '@/types/viewScope';

export const FULL_ARCHIVE: ViewScope = Object.freeze({ kind: ViewScopeKind.FullArchive });

export function getCaseScope(caseId: string): ViewScope {
    return { kind: ViewScopeKind.Case, caseId };
}

export function getBookmarkScope(bookmark: Bookmark): ViewScope {
    return { kind: ViewScopeKind.Bookmark, bookmark };
}

/**
 * Two scopes are the same when they name the same case or the same bookmark: a bookmark edited
 * since it was opened is still the bookmark the view is cut to.
 */
export function scopeKey(scope: ViewScope): string {
    switch (scope.kind) {
        case ViewScopeKind.Case:
            return `case:${scope.caseId}`;
        case ViewScopeKind.Bookmark:
            return `bookmark:${scope.bookmark.id}`;
        default:
            return ViewScopeKind.FullArchive;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isBookmark(value: unknown): value is Bookmark {
    if (!isRecord(value)) {
        return false;
    }
    const { id, deviceId, startTimeMs, durationMs, description, tags } = value;
    return typeof id === 'string' && typeof deviceId === 'string' &&
        typeof startTimeMs === 'number' && typeof durationMs === 'number' &&
        typeof description === 'string' && Array.isArray(tags);
}

/**
 * The scope the local props of the view panel hold. They are JSON a reload reads back, possibly
 * written by an older build, so anything that is not a whole scope is the full archive.
 */
export function readScope(value: unknown): ViewScope {
    if (!isRecord(value)) {
        return FULL_ARCHIVE;
    }
    if (value.kind === ViewScopeKind.Case && typeof value.caseId === 'string') {
        return getCaseScope(value.caseId);
    }
    if (value.kind === ViewScopeKind.Bookmark && isBookmark(value.bookmark)) {
        return getBookmarkScope(value.bookmark);
    }
    return FULL_ARCHIVE;
}
